// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import arrowIcon from '../../assets/icons/white/back_small.png';
import './SwipeableContainer.scss';

export default function SwipeableContainer({
  children,
  onSwipeLeft,
  onSwipeRight,
  swipeThreshold = 100,
  overlayColor = 'rgba(31, 31, 31, 0.8)',
  className,
  style,
  resetKey, // When this changes, zoom/pan will be reset
  ...rest
}) {
  const [touchStartX, setTouchStartX] = React.useState(null);
  const [touchPosition, setTouchPosition] = React.useState(0);
  const containerRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);
  const [translateX, setTranslateX] = React.useState(0);
  const [translateY, setTranslateY] = React.useState(0);
  const [initialDist, setInitialDist] = React.useState(null);
  const [prevScale, setPrevScale] = React.useState(1);
  const [prevTranslateX, setPrevTranslateX] = React.useState(0);
  const [prevTranslateY, setPrevTranslateY] = React.useState(0);
  const [prevTouch, setPrevTouch] = React.useState(null);

  // Reset zoom/pan when resetKey changes (e.g., when changing images)
  React.useEffect(() => {
    if (resetKey !== undefined) {
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
      setTouchPosition(0);
    }
  }, [resetKey]);

  const clampValues = (s, tx, ty) => {
    const content = innerRef.current;
    const container = containerRef.current;
    if (!content || !container) return { tx, ty };
    const contentW = content.clientWidth;
    const contentH = content.clientHeight;
    const contW = container.clientWidth;
    const contH = container.clientHeight;
    const scaledW = contentW * s;
    const scaledH = contentH * s;
    const minTx = scaledW > contW ? contW - scaledW : 0;
    const maxTx = 0;
    const minTy = scaledH > contH ? contH - scaledH : 0;
    const maxTy = 0;
    return {
      tx: Math.max(minTx, Math.min(maxTx, tx)),
      ty: Math.max(minTy, Math.min(maxTy, ty)),
    };
  };

  function handleTouchStart(e) {
    const touchesLength = e.touches.length;
    setTouchPosition(0);
    if (touchesLength === 1) {
      setTouchStartX(e.touches[0].clientX);
      setPrevTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      setInitialDist(null);
    } else if (touchesLength === 2) {
      const x1 = e.touches[0].clientX;
      const y1 = e.touches[0].clientY;
      const x2 = e.touches[1].clientX;
      const y2 = e.touches[1].clientY;
      const dist = Math.hypot(x1 - x2, y1 - y2);
      setInitialDist(dist);
      setPrevScale(scale);
      setPrevTranslateX(translateX);
      setPrevTranslateY(translateY);
      setTouchStartX(null);
    } else {
      setTouchStartX(null);
      setInitialDist(null);
    }
  }

  function handleTouchMove(e) {
    const touchesLength = e.touches.length;

    if (touchesLength === 1 && prevTouch) {
      if (scale > 1) {
        const dx = e.touches[0].clientX - prevTouch.x;
        const dy = e.touches[0].clientY - prevTouch.y;
        let newTx = translateX + dx;
        let newTy = translateY + dy;
        const { tx, ty } = clampValues(scale, newTx, newTy);
        setTranslateX(tx);
        setTranslateY(ty);
        setPrevTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      } else if (touchStartX !== null) {
        const currentX = e.touches[0].clientX;
        setTouchPosition(currentX - touchStartX);
      }
    } else if (touchesLength === 2 && initialDist !== null) {
      const rect = containerRef.current.getBoundingClientRect();
      const x1 = e.touches[0].clientX;
      const y1 = e.touches[0].clientY;
      const x2 = e.touches[1].clientX;
      const y2 = e.touches[1].clientY;
      const currentDist = Math.hypot(x1 - x2, y1 - y2);
      const distRatio = currentDist / initialDist;
      const newScale = Math.max(1, Math.min(4, prevScale * distRatio));
      const centerX = (x1 + x2) / 2 - rect.left;
      const centerY = (y1 + y2) / 2 - rect.top;
      const scaleRatio = newScale / prevScale;
      let newTx = (1 - scaleRatio) * centerX + scaleRatio * prevTranslateX;
      let newTy = (1 - scaleRatio) * centerY + scaleRatio * prevTranslateY;
      const { tx, ty } = clampValues(newScale, newTx, newTy);
      setScale(newScale);
      setTranslateX(tx);
      setTranslateY(ty);
    }
  }

  function handleTouchEnd(e) {
    const remainingTouches = e.touches.length;
    if (remainingTouches === 0) {
      if (touchStartX !== null) {
        const deltaX = e.changedTouches[0].clientX - touchStartX;
        if (scale === 1 && Math.abs(deltaX) > swipeThreshold) {
          if (deltaX > 0 && onSwipeLeft) onSwipeLeft();
          else if (deltaX < 0 && onSwipeRight) onSwipeRight();
        }
      }
      setTouchStartX(null);
      setTouchPosition(0);
      setInitialDist(null);
      setPrevTouch(null);
    } else if (remainingTouches === 1 && initialDist !== null) {
      setInitialDist(null);
      setTouchStartX(null);
      setPrevTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else {
      setInitialDist(null);
      setTouchStartX(null);
      setTouchPosition(0);
    }
  }

  const fadeProgress = Math.min(Math.abs(touchPosition) / swipeThreshold, 1);
  const isSwipingLeft = touchPosition < 0;

  return (
    <div
      ref={containerRef}
      className={`swipeable-container ${className || ''}`}
      style={{
        transform: `translateX(${touchPosition}px)`,
        transition: touchStartX ? 'none' : '0.3s',
        ...style,
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      {...rest}
    >
      <div className='swipeable-overlay' style={{ backgroundColor: overlayColor, opacity: fadeProgress }} />
      <div
        className='swipeable-arrow'
        style={{
          [isSwipingLeft ? 'right' : 'left']: '2rem',
          opacity: fadeProgress,
          transform: `translateY(-50%) ${isSwipingLeft ? 'rotate(180deg)' : ''}`,
        }}
      >
        <img src={arrowIcon} alt='Swipe arrow' />
      </div>
      <div
        ref={innerRef}
        className='zoomable-content'
        style={{
          width: '100%',
          height: '100%',
          transformOrigin: '0 0',
          transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
